// CashierCard.jsx — Real-time customer lookup with AI-generated personalized offer.
// Cashier types a phone or partial name → app fetches behavioral profile + Claude offer.
import React, { useState, useEffect, useRef } from "react";
import { api, formatErr } from "./lib/api";
import { fm } from "./lib/analytics";
import { startServiceConversation } from "./lib/startServiceConversation";

const SEGMENT_BADGE = {
  champions:    { label: "🏆 بطل",          color: "var(--success)" },
  loyal:        { label: "💙 وفي",          color: "var(--info)" },
  new:          { label: "🌱 جديد",          color: "var(--gold)" },
  potential:    { label: "✨ محتمل",          color: "var(--brown)" },
  at_risk:      { label: "⚠ مهدد",            color: "var(--warn)" },
  lost:         { label: "💔 مفقود",          color: "var(--danger)" },
  "59X — مستنفذ":     { label: "🚫 59X — مستنفذ",   color: "var(--warn)" },
  "59 مجاني فقط":     { label: "🆓 59 مجاني فقط",   color: "var(--danger)" },
};

function SegmentBadge({ seg }) {
  const m = SEGMENT_BADGE[seg] || { label: seg, color: "var(--muted)" };
  return (
    <span data-testid="cashier-segment-badge" style={{ padding: "3px 10px", borderRadius: 999, background: m.color, color: "#fff", fontSize: 11.5, fontWeight: 700 }}>
      {m.label}
    </span>
  );
}

export default function CashierCard() {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [singleCode, setSingleCode] = useState(null);
  const [codeBusy, setCodeBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (overrideQuery) => {
    const q = (overrideQuery ?? query).trim();
    if (!q || q.length < 2 || busy) return;
    setBusy(true); setErr(null); setResult(null); setSingleCode(null);
    try {
      const { data } = await api.post("/customer/lookup", { query: q, use_ai: true });
      setResult(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const pickMatch = (m) => {
    setQuery(m.phone || m.name);
    submit(m.phone || m.name);
  };

  const reset = () => { setQuery(""); setResult(null); setErr(null); inputRef.current?.focus(); };

  const customer = result?.customer;
  const offer = result?.offer;
  const matches = result?.matches || [];

  return (
    <div data-testid="cashier-card-page" style={{ maxWidth: 920, margin: "0 auto" }}>
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>🎟 بطاقة العميل — أداة الكاشير</h3>
        <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.8 }}>
          ادخل جوال العميل أو جزء من اسمه عند وصوله للكاشير. سيظهر ملفه السلوكي مع عرض شخصي مولّد بالذكاء الاصطناعي يُعرض عليه مباشرة.
        </p>
      </div>

      <div className="card" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <input
          ref={inputRef}
          data-testid="cashier-lookup-input"
          type="search"
          placeholder="جوال (0501234567) أو اسم..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          disabled={busy}
          style={{ flex: "1 1 280px", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 16, fontFamily: "inherit", direction: "ltr", textAlign: "right" }}
        />
        <button type="button" className="btn btn-success" onClick={() => submit()} disabled={busy || query.trim().length < 2} data-testid="cashier-lookup-btn">
          {busy ? "⏳ بحث وتحليل..." : "🔍 بحث"}
        </button>
        {(result || err) && (
          <button type="button" className="btn btn-soft" onClick={reset} data-testid="cashier-reset-btn">
            ✖ مسح
          </button>
        )}
      </div>

      {err && <div className="al alr" data-testid="cashier-error" style={{ marginBottom: 14 }}>⚠ {err}</div>}

      {matches.length > 0 && !customer && (
        <div className="card" data-testid="cashier-matches">
          <div className="st">🔎 وُجد {matches.length} عميل بهذا الاسم — اختر واحداً:</div>
          {matches.map((m, i) => (
            <button
              key={(m.phone || m.name) + i}
              type="button"
              data-testid={`cashier-match-${i}`}
              onClick={() => pickMatch(m)}
              className="match-row"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", padding: 12, border: "1px solid var(--border)", borderRadius: 8, marginBottom: 6, background: "var(--card)", cursor: "pointer", fontSize: 13 }}
            >
              <div style={{ textAlign: "right", flex: 1 }}>
                <div style={{ fontWeight: 700, color: "var(--brown-deep)" }}>{m.name || "(بدون اسم)"}</div>
                <div style={{ fontSize: 11.5, color: "var(--muted)" }}>{m.phone || "—"} · آخر زيارة: {m.last_date || "—"}</div>
              </div>
              <div style={{ fontSize: 12, color: "var(--success)", fontWeight: 700 }}>{m.visits} زيارة</div>
            </button>
          ))}
        </div>
      )}

      {customer && (
        <>
          {/* Profile card */}
          <div className="card" data-testid="cashier-profile" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "var(--brown-deep)" }}>👤 {customer.name}</div>
                <div style={{ fontSize: 12.5, color: "var(--muted)", direction: "ltr", textAlign: "right" }}>📞 {customer.phone || "—"}</div>
              </div>
              <SegmentBadge seg={customer.segment} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
              <Stat label="إجمالي الزيارات" value={fm(customer.visits_total)} sub={`${customer.paid_visits} مدفوعة · ${customer.free_visits} مجانية`} />
              <Stat label="إجمالي الإنفاق" value={fm(Math.round(customer.total_spent)) + " ر"} sub="من الفواتير المدفوعة" color="var(--success)" />
              <Stat label="متوسط الفاتورة" value={customer.avg_invoice.toFixed(1) + " ر"} sub="عند الدفع" color={customer.avg_invoice >= 14 ? "var(--success)" : "var(--warn)"} />
              <Stat label="آخر زيارة" value={customer.last_date || "—"} sub={customer.days_since_last_visit !== null ? `قبل ${customer.days_since_last_visit} يوم` : ""} color={customer.days_since_last_visit > 30 ? "var(--danger)" : ""} />
              <Stat label="أول زيارة" value={customer.first_date || "—"} sub="بداية العلاقة" />
              {customer.is_59x && <Stat label="🚫 الحالة" value="59 X" sub="استنفد كل الأكواب المجانية" color="var(--warn)" />}
              {customer.is_59 && !customer.is_59x && <Stat label="✅ الحالة" value="59 نشط" sub="لا يزال يحصل على أكواب" color="var(--success)" />}
            </div>

            {/* Favorite times */}
            <div className="g2" style={{ marginTop: 14, gap: 10 }}>
              <div data-testid="cashier-top-hours">
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown)", marginBottom: 6 }}>⏰ أكثر الساعات حضوراً</div>
                {customer.top_hours.length > 0 ? customer.top_hours.map((h) => (
                  <div key={h.hour} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", background: "var(--beige)", marginBottom: 4, borderRadius: 6, fontSize: 12 }}>
                    <span>{h.label}</span>
                    <span style={{ color: "var(--success)", fontWeight: 700 }}>{h.count} زيارة</span>
                  </div>
                )) : <div style={{ fontSize: 12, color: "var(--muted)" }}>لا بيانات</div>}
              </div>
              <div data-testid="cashier-top-weekdays">
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown)", marginBottom: 6 }}>📅 أكثر الأيام حضوراً</div>
                {customer.top_weekdays.length > 0 ? customer.top_weekdays.map((w) => (
                  <div key={w.weekday} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", background: "var(--beige)", marginBottom: 4, borderRadius: 6, fontSize: 12 }}>
                    <span>{w.label}</span>
                    <span style={{ color: "var(--success)", fontWeight: 700 }}>{w.count} زيارة</span>
                  </div>
                )) : <div style={{ fontSize: 12, color: "var(--muted)" }}>لا بيانات</div>}
              </div>
            </div>

            {/* Visit history — last 10 visits */}
            {customer.visit_history && customer.visit_history.length > 0 && (
              <div data-testid="cashier-visit-history" style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown)", marginBottom: 6 }}>📜 سجل آخر {customer.visit_history.length} زيارة</div>
                <div className="tw">
                  <table>
                    <thead><tr><th>التاريخ</th><th>الساعة</th><th>الشفت</th><th>المبلغ</th><th>الحالة</th></tr></thead>
                    <tbody>
                      {customer.visit_history.map((v, i) => (
                        <tr key={v.date + ":" + v.hour + ":" + i}>
                          <td><b>{v.date}</b></td>
                          <td>{v.hour !== null ? `${String(v.hour).padStart(2, "0")}:00` : "—"}</td>
                          <td>{v.shift || "—"}</td>
                          <td style={{ color: v.is_free ? "var(--warn)" : "var(--success)", fontWeight: 600 }}>
                            {v.is_free ? "مجاني" : fm(Math.round(v.total)) + " ر"}
                          </td>
                          <td>{v.is_free ? "🆓" : v.total > 0 ? "💰 مدفوعة" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* AI Offer card — the centerpiece */}
          {offer && !offer.error && (
            <div data-testid="cashier-ai-offer" className="card" style={{ background: "linear-gradient(135deg, var(--beige) 0%, var(--card) 100%)", border: "2px solid var(--gold)", padding: 18 }}>
              <div style={{ fontSize: 12.5, color: "var(--brown)", fontWeight: 700, marginBottom: 12 }}>
                🤖 عرض شخصي مولَّد بالذكاء الاصطناعي — اقرأه على العميل:
              </div>

              {offer.greeting && (
                <div style={{ fontSize: 20, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 10, lineHeight: 1.5 }}>
                  {offer.greeting}
                </div>
              )}

              {offer.offer_message && (
                <div style={{ fontSize: 18, color: "var(--brown-deep)", lineHeight: 1.7, padding: 14, background: "rgba(255,255,255,0.6)", borderRadius: 10, border: "1px dashed var(--gold)", marginBottom: 12 }}>
                  🎁 {offer.offer_message}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 12 }}>
                {typeof offer.suggested_discount_pct === "number" && offer.suggested_discount_pct > 0 && (
                  <div style={{ padding: 10, background: "var(--success)", color: "#fff", borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>خصم مقترح</div>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{offer.suggested_discount_pct}%</div>
                  </div>
                )}
                {offer.suggested_freebie && offer.suggested_freebie !== "null" && (
                  <div style={{ padding: 10, background: "var(--gold)", color: "#fff", borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>هدية مقترحة</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>{offer.suggested_freebie}</div>
                  </div>
                )}
                {offer.best_time_to_send && (
                  <div style={{ padding: 10, background: "var(--info)", color: "#fff", borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>أفضل وقت لإرسال WhatsApp</div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 2 }}>{offer.best_time_to_send}</div>
                  </div>
                )}
              </div>

              {offer.reasoning && (
                <div style={{ fontSize: 11.5, color: "var(--muted)", borderTop: "1px solid var(--border)", paddingTop: 10, lineHeight: 1.7 }}>
                  💭 <b>سبب الاختيار:</b> {offer.reasoning}
                </div>
              )}

              {customer.phone && offer.offer_message && (
                <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {/* CS-1 (بند ج2) — كان wa.me (واتساب شخصي خارج المنصة):
                      لا تسجيل ولا رقم شركة. صار «محادثة جديدة» بالرقم معبّأ
                      داخل الصندوق الموحّد بمصدر مسجَّل. */}
                  <button
                    type="button"
                    onClick={() => startServiceConversation({
                      phone: customer.phone, source: "cashier_card_offer",
                    })}
                    className="btn btn-success"
                    data-testid="cashier-send-whatsapp"
                    style={{ border: "none", cursor: "pointer" }}
                  >
                    📩 ابدأ محادثة خدمة بالعرض
                  </button>
                  <button
                    type="button"
                    className="btn btn-soft"
                    onClick={() => navigator.clipboard?.writeText(`${offer.greeting || ""}\n${offer.offer_message}${singleCode ? `\n🎟 كود الخصم: ${singleCode}` : ""}`)}
                    data-testid="cashier-copy-offer"
                  >
                    📋 نسخ النص
                  </button>
                  {/* v121 — كود مستقل لعميل واحد من بنك الأكواد */}
                  {!singleCode ? (
                    <button
                      type="button"
                      className="btn btn-soft"
                      disabled={codeBusy}
                      data-testid="cashier-attach-code"
                      style={{ border: "1px dashed #7C3AED", color: "#7C3AED", fontWeight: 700 }}
                      onClick={async () => {
                        setCodeBusy(true);
                        try {
                          const { data } = await api.post("/discount-codes/consume-single", {
                            phone: customer.phone,
                            discount_pct: offer.suggested_discount_pct || null,
                          });
                          setSingleCode(data.code);
                        } catch (e) {
                          alert(formatErr(e.response?.data?.detail) || "لا توجد أكواد متاحة");
                        } finally { setCodeBusy(false); }
                      }}
                    >
                      {codeBusy ? "⏳..." : "🎟 أرفق كود خصم مستقل لهذا العميل"}
                    </button>
                  ) : (
                    <span data-testid="cashier-single-code" style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(124,58,237,0.08)", color: "#7C3AED", fontWeight: 800, fontSize: 12.5 }} dir="ltr">
                      🎟 {singleCode}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {offer?.error && (
            <div className="al alo" data-testid="cashier-ai-error">
              ⚠ تعذّر توليد العرض بالذكاء الاصطناعي: {offer.error}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ padding: 10, background: "var(--beige)", borderRadius: 8 }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: color || "var(--brown-deep)" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
